import { EnrollmentStatus } from '@/pipeline/stateMachine';
import { PersonalDocuments, SponsorDocuments } from '../modules/documents/state';
import { Campus, Course } from '../modules/enrollment/state';
import {
  AcademicFormation,
  AddressInfo,
  IdentificationDocument,
  IdentificationDocumentFinancialSponsor,
  PersonalData,
  PersonalDataFinancialSponsor,
  SponsorAddressInfo,
} from '../modules/studentData/state';
import { GetAdmission } from './queries/westerosAdmissionsQueries';
import { GetEnrollment } from './queries/westerosArcaneumQueries';

export interface NormalizedAdmission {
  course?: Course;
  campus?: Campus;
}

export interface NormalizedEnrollment {
  partnerCode?: string;
  enrollmentId?: number;
  areDocumentsSent?: boolean;
  personalData?: PersonalData;
  isContractAccepted?: boolean;
  studentAddress?: AddressInfo;
  studentSignupMethod?: string;
  currentPaymentMethod?: string;
  currentPaymentStatus?: string;
  currentPaymentRecurrentCreditCard?: boolean;
  sponsorAddress?: SponsorAddressInfo;
  enrollmentStatus?: EnrollmentStatus;
  academicFormation?: AcademicFormation;
  personalDocuments?: PersonalDocuments;
  financialSponsorDocuments?: SponsorDocuments;
  identificationDocument?: IdentificationDocument;
  personalDataFinancialSponsor?: PersonalDataFinancialSponsor;
  identificationFinancialSponsor?: IdentificationDocumentFinancialSponsor;
  backofficeStudentAcademicRecord?: string;
}

export function normalizeAdmission(admissionsResult?: GetAdmission): NormalizedAdmission {
  const course: Course = {
    id: admissionsResult?.course?.id,
    name: admissionsResult?.course?.name,
    descomplicaProviderCode: admissionsResult?.course?.descomplicaProviderCode,
    descomplicaCurriculumId: admissionsResult?.course?.descomplicaCurriculumId,
  };

  const campus: Campus = {
    id: admissionsResult?.campus?.id,
    name: admissionsResult?.campus?.name,
    city: admissionsResult?.campus?.city,
    state: admissionsResult?.campus?.state,
  };

  return {
    course,
    campus,
  };
}

export function normalizeEnrollment(
  enrollmentResult: GetEnrollment | undefined,
): NormalizedEnrollment {
  const enrollmentId: number | undefined = enrollmentResult?.id;
  const areDocumentsSent = enrollmentResult?.areDocumentsSent;
  const enrollmentStatus = enrollmentResult?.enrollmentStatus;
  const isContractAccepted = enrollmentResult?.isContractAccepted;
  const partnerCode = enrollmentResult?.partnerCode;
  const backofficeStudentAcademicRecord = enrollmentResult?.backofficeStudentAcademicRecord;

  const currentPaymentMethod = enrollmentResult?.currentPayment?.paymentMethod;
  const currentPaymentStatus = enrollmentResult?.currentPayment?.paymentStatus;
  const currentPaymentRecurrentCreditCard = enrollmentResult?.currentPayment?.isRecurrentCreditCard;

  const student = enrollmentResult?.student;
  const personalData: PersonalData | undefined = {
    cpf: student?.cpf || undefined,
    race: student?.race,
    email: student?.email,
    phone: student?.phone,
    gender: student?.gender,
    fullName: student?.fullName,
    birthDate: student?.birthDate
      ? new Date(student?.birthDate).toLocaleDateString('pt-BR')
      : undefined,
    nationality: student?.nationality,
    placeOfBirth: student?.placeOfBirth,
    householdIncome: student?.householdIncome,
    cityBirth: student?.cityBirth,
  };
  const academicFormation: AcademicFormation | undefined = {
    city: student?.schoolCity,
    schoolCategory: student?.schoolCategory,
    year: student?.schoolFormationYear,
  };
  const identificationDocument: IdentificationDocument | undefined = {
    rg: student?.rg || undefined,
    issuer: student?.rgIssuer,
    issueState: student?.rgIssueState,
    motherName: student?.motherName,
    fatherName: student?.fatherName,
    issueDate: student?.rgIssueDate
      ? new Date(student?.rgIssueDate).toLocaleDateString('pt-BR')
      : undefined,
  };
  const studentAddress: AddressInfo | undefined = {
    state: student?.address?.state,
    street: student?.address?.streetName,
    number: student?.address?.streetNumber,
    zipCode: student?.address?.zipCode,
    complement: student?.address?.complement,
    neighborhood: student?.address?.neighborhood,
  };
  const studentSignupMethod: string | undefined = student?.signupMethod;

  const financialSponsor = enrollmentResult?.financialSponsor;
  const personalDataFinancialSponsor: PersonalDataFinancialSponsor | undefined = {
    cpf: financialSponsor?.cpf || undefined,
    email: financialSponsor?.email,
    phone: financialSponsor?.phone,
    fullName: financialSponsor?.fullName,
    birthDate: financialSponsor?.birthDate
      ? new Date(financialSponsor?.birthDate).toLocaleDateString('pt-BR')
      : undefined,
  };
  const identificationFinancialSponsor: IdentificationDocumentFinancialSponsor | undefined = {
    rg: financialSponsor?.rg || undefined,
    issuer: financialSponsor?.rgIssuer,
    issueState: financialSponsor?.rgIssueState,
    issueDate: financialSponsor?.rgIssueDate
      ? new Date(financialSponsor?.rgIssueDate).toLocaleDateString('pt-BR')
      : undefined,
  };

  const sponsorAddress: SponsorAddressInfo | undefined = {
    sponsorState: financialSponsor?.address?.state,
    sponsorStreet: financialSponsor?.address?.streetName,
    sponsorNumber: financialSponsor?.address?.streetNumber,
    sponsorZipCode: financialSponsor?.address?.zipCode,
    sponsorComplement: financialSponsor?.address?.complement,
    sponsorNeighborhood: financialSponsor?.address?.neighborhood,
  };

  // ToDo: Do a proper 'de-para' if we allow the user to delete and/or reupload the docs.
  // const studentDocs = student?.documents;
  const personalDocuments: PersonalDocuments = {
    rg: undefined,
    cpf: undefined,
    selfie: undefined,
    enemGradeProof: undefined,
    highSchoolTranscript: undefined,
    highSchoolDiploma: undefined,
  };

  // ToDo: Do a proper 'de-para' if we allow the user to delete and/or reupload the docs.
  // const sponsorDocs = financialSponsor?.documents;
  const financialSponsorDocuments: SponsorDocuments = {
    rgSponsor: undefined,
    cpfSponsor: undefined,
    selfieSponsor: undefined,
  };

  const enrollment: NormalizedEnrollment = {
    partnerCode,
    enrollmentId,
    personalData,
    studentAddress,
    studentSignupMethod,
    sponsorAddress,
    areDocumentsSent,
    enrollmentStatus,
    academicFormation,
    personalDocuments,
    isContractAccepted,
    currentPaymentMethod,
    currentPaymentStatus,
    currentPaymentRecurrentCreditCard,
    identificationDocument,
    financialSponsorDocuments,
    personalDataFinancialSponsor,
    identificationFinancialSponsor,
    backofficeStudentAcademicRecord,
  };

  return enrollment;
}
